import React, { useEffect, useMemo, useRef, useState } from 'react';
import TagChip from './TagChip';
import { normalizeTagValue } from '../utils/tagUtils';
import { buildTagGroups, buildTagGroupsFromRaw } from '../utils/tagGroupUtils';

const INDEX_TABS = Object.freeze([
    { id: 'subjects', label: 'Subjects' },
    { id: 'geography', label: 'Geography' },
    { id: 'persons', label: 'Persons' },
]);

const collectCategoryTags = (groups = []) => {
    const canonical = new Map();

    (Array.isArray(groups) ? groups : []).forEach((group) => {
        const tags = Array.isArray(group?.subTags) ? group.subTags : [];
        tags.forEach((tag) => {
            const label = typeof tag === 'string' ? tag : String(tag ?? '');
            const normalized = normalizeTagValue(label);
            if (!normalized || canonical.has(normalized)) {
                return;
            }
            canonical.set(normalized, label);
        });
    });

    return [...canonical.values()].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
};

const Sidebar = ({
    tags,
    tagGroups: rawTagGroups,
    selectedTags,
    selectedTagSet,
    onTagClick,
    onSelectTags,
    onClearFilters,
}) => {
    const normalizedSelectedTags = selectedTagSet instanceof Set
        ? selectedTagSet
        : new Set(Array.isArray(selectedTags) ? selectedTags.map(normalizeTagValue) : []);

    const scrollCardRef = useRef(null);
    const rafIdRef = useRef(null);

    useEffect(() => {
        const updateSidebarHeight = () => {
            const node = scrollCardRef.current;
            if (!node) {
                return;
            }

            const rect = node.getBoundingClientRect();
            const top = Math.max(0, rect.top);
            const bottomGap = 16;
            const available = window.innerHeight - top - bottomGap;
            const height = Math.max(240, Math.floor(available));
            node.style.setProperty('--sidebar-card-height', `${height}px`);
        };

        const scheduleUpdate = () => {
            if (rafIdRef.current != null) {
                return;
            }
            rafIdRef.current = requestAnimationFrame(() => {
                rafIdRef.current = null;
                updateSidebarHeight();
            });
        };

        scheduleUpdate();
        window.addEventListener('resize', scheduleUpdate);
        window.addEventListener('scroll', scheduleUpdate, { passive: true });

        return () => {
            window.removeEventListener('resize', scheduleUpdate);
            window.removeEventListener('scroll', scheduleUpdate);
            if (rafIdRef.current != null) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = null;
            }
        };
    }, []);

    const [activeIndexTab, setActiveIndexTab] = useState('subjects');

    const classifiedRawTagGroups = useMemo(() => {
        const source = Array.isArray(rawTagGroups) ? rawTagGroups : [];
        const buckets = {
            subjects: [],
            geography: [],
            persons: [],
        };

        source.forEach((group) => {
            if (!group) {
                return;
            }
            const category = typeof group.category === 'string' ? group.category.trim().toLowerCase() : '';
            if (category === 'geography') {
                buckets.geography.push(group);
                return;
            }
            if (category === 'persons') {
                buckets.persons.push(group);
                return;
            }
            buckets.subjects.push(group);
        });

        return buckets;
    }, [rawTagGroups]);

    const subjectTagGroups = useMemo(() => buildTagGroups(classifiedRawTagGroups.subjects), [classifiedRawTagGroups.subjects]);
    const geographyTagGroups = useMemo(() => buildTagGroupsFromRaw(classifiedRawTagGroups.geography), [classifiedRawTagGroups.geography]);
    const personTagGroups = useMemo(() => buildTagGroupsFromRaw(classifiedRawTagGroups.persons), [classifiedRawTagGroups.persons]);

    const geographyTags = useMemo(() => collectCategoryTags(geographyTagGroups), [geographyTagGroups]);
    const personTags = useMemo(() => collectCategoryTags(personTagGroups), [personTagGroups]);

    const activeIndexTabLabel = useMemo(() => (
        INDEX_TABS.find((tab) => tab.id === activeIndexTab)?.label ?? 'Subjects'
    ), [activeIndexTab]);

    const hiddenTagSet = useMemo(() => {
        const set = new Set();
        geographyTags.forEach((tag) => {
            const normalized = normalizeTagValue(tag);
            if (normalized) {
                set.add(normalized);
            }
        });
        personTags.forEach((tag) => {
            const normalized = normalizeTagValue(tag);
            if (normalized) {
                set.add(normalized);
            }
        });
        return set;
    }, [geographyTags, personTags]);

    const subjectTags = useMemo(() => {
        const source = Array.isArray(tags) ? tags : [];
        if (hiddenTagSet.size === 0) {
            return source;
        }

        return source.filter((tag) => {
            const normalized = normalizeTagValue(tag);
            return normalized && !hiddenTagSet.has(normalized);
        });
    }, [tags, hiddenTagSet]);

    const indexPanel = useMemo(() => {
        if (activeIndexTab === 'geography') {
            return { kind: 'tags', items: geographyTags };
        }
        if (activeIndexTab === 'persons') {
            return { kind: 'tags', items: personTags };
        }
        return { kind: 'groups', items: subjectTagGroups };
    }, [activeIndexTab, geographyTags, personTags, subjectTagGroups]);

    const [chosenGroupIds, setChosenGroupIds] = useState([]);

    useEffect(() => {
        if (Array.isArray(selectedTags) && selectedTags.length === 0 && chosenGroupIds.length > 0) {
            setChosenGroupIds([]);
        }
    }, [selectedTags, chosenGroupIds]);

    const groupSelectionState = useMemo(() => {
        const map = new Map();
        subjectTagGroups.forEach((group) => {
            const normalizedGroupTags = Array.isArray(group.subTags)
                ? group.subTags.map((tag) => normalizeTagValue(tag)).filter(Boolean)
                : [];

            if (normalizedGroupTags.length === 0) {
                map.set(group.id, { anySelected: false, allSelected: false, tags: [] });
                return;
            }

            const anySelected = normalizedGroupTags.some((tag) => normalizedSelectedTags.has(tag));
            const allSelected = normalizedGroupTags.every((tag) => normalizedSelectedTags.has(tag));
            map.set(group.id, { anySelected, allSelected, tags: normalizedGroupTags });
        });
        return map;
    }, [subjectTagGroups, normalizedSelectedTags]);

    const chosenGroupIdSet = useMemo(() => new Set(chosenGroupIds), [chosenGroupIds]);
    const groupById = useMemo(() => {
        const map = new Map();
        subjectTagGroups.forEach((group) => map.set(group.id, group));
        return map;
    }, [subjectTagGroups]);

    const { pinnedSections, secondaryTags } = useMemo(() => {
        const source = Array.isArray(subjectTags) ? subjectTags : [];
        const sections = [];
        const pinned = new Set();

        chosenGroupIds.forEach((groupId) => {
            const group = groupById.get(groupId);
            if (!group || !Array.isArray(group.subTags) || group.subTags.length === 0) {
                return;
            }

            const selectedInGroup = [];
            group.subTags.forEach((tag) => {
                const label = typeof tag === 'string' ? tag : String(tag ?? '');
                const normalized = normalizeTagValue(label);
                if (normalized && normalizedSelectedTags.has(normalized)) {
                    selectedInGroup.push(tag);
                    pinned.add(normalized);
                }
            });

            if (selectedInGroup.length > 0) {
                sections.push({ groupId, tags: selectedInGroup });
            }
        });

        const rest = [];
        source.forEach((tag) => {
            const label = typeof tag === 'string' ? tag : String(tag ?? '');
            const normalized = normalizeTagValue(label);
            if (!normalized || !pinned.has(normalized)) {
                rest.push(tag);
            }
        });

        return { pinnedSections: sections, secondaryTags: rest };
    }, [subjectTags, chosenGroupIds, groupById, normalizedSelectedTags]);

    const handleGroupClick = (groupId) => {
        const state = groupSelectionState.get(groupId);
        if (!state || state.tags.length === 0) {
            return;
        }

        const next = new Set(normalizedSelectedTags);
        const isChosen = chosenGroupIdSet.has(groupId);
        if (isChosen) {
            state.tags.forEach((tag) => next.delete(tag));
            setChosenGroupIds((prev) => prev.filter((id) => id !== groupId));
        } else {
            state.tags.forEach((tag) => next.add(tag));
            setChosenGroupIds((prev) => (prev.includes(groupId) ? prev : [...prev, groupId]));
        }

        onSelectTags?.([...next]);
    };

    return (
        <aside className="sidebar">
            <div className="sidebar-card sidebar-card--groups" data-label="INDEX">
                <div className="tag-filter">
                    <div className="index-tabs" role="tablist" aria-label="Index categories">
                        {INDEX_TABS.map((tab) => {
                            const isActive = tab.id === activeIndexTab;
                            return (
                                <button
                                    key={tab.id}
                                    type="button"
                                    className={`index-tab${isActive ? ' active' : ''}`}
                                    role="tab"
                                    aria-selected={isActive}
                                    onClick={() => setActiveIndexTab(tab.id)}
                                >
                                    {tab.label}
                                </button>
                            );
                        })}
                    </div>

                    {indexPanel.items.length === 0 ? (
                        <p className="index-empty">
                            [NO {activeIndexTabLabel.toUpperCase()} AVAILABLE]
                        </p>
                    ) : indexPanel.kind === 'groups' ? (
                        <div className="tag-group-list" role="tabpanel" aria-label={`${activeIndexTabLabel} groups`}>
                            {indexPanel.items.map((group) => {
                                const groupId = group.id;
                                const isDisabled = !Array.isArray(group.subTags) || group.subTags.length === 0;

                                return (
                                    <TagChip
                                        key={groupId}
                                        label={group.label}
                                        value={groupId}
                                        isActive={chosenGroupIdSet.has(groupId)}
                                        isInteractive
                                        disabled={isDisabled}
                                        onToggle={handleGroupClick}
                                    />
                                );
                            })}
                        </div>
                    ) : (
                        <div className="tag-group-list" role="tabpanel" aria-label={`${activeIndexTabLabel} tags`}>
                            {indexPanel.items.map((tag) => {
                                const label = typeof tag === 'string' ? tag : String(tag ?? '');
                                const normalizedLabel = normalizeTagValue(label);
                                const isActive = normalizedSelectedTags.has(normalizedLabel);

                                return (
                                    <TagChip
                                        key={normalizedLabel || label}
                                        label={label}
                                        value={normalizedLabel}
                                        isActive={isActive}
                                        isInteractive
                                        onToggle={onTagClick}
                                    />
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <div className="sidebar-card sidebar-card--subtags" data-label="TAGS" ref={scrollCardRef}>
                <div className="tag-filter">
                    {/* <h2>Sub-Tags</h2> */}
                    <div className="tag-list" aria-label="Sub-tags">
                        {subjectTags.length === 0 ? (
                            <p className="no-tags">[No Tags Available]</p>
                        ) : (
                            <>
                                {pinnedSections.map((section, sectionIndex) => {
                                    const showDividerAfter = sectionIndex < pinnedSections.length - 1 || secondaryTags.length > 0;
                                    return (
                                        <React.Fragment key={section.groupId}>
                                            {section.tags.map((tag) => {
                                                const label = typeof tag === 'string' ? tag : String(tag ?? '');
                                                const normalizedLabel = normalizeTagValue(label);
                                                const isActive = normalizedSelectedTags.has(normalizedLabel);

                                                return (
                                                    <TagChip
                                                        key={normalizedLabel || label}
                                                        label={label}
                                                        value={normalizedLabel}
                                                        isActive={isActive}
                                                        isInteractive
                                                        onToggle={onTagClick}
                                                    />
                                                );
                                            })}
                                            {showDividerAfter && (
                                                <div className="tag-list-divider" aria-hidden="true" />
                                            )}
                                        </React.Fragment>
                                    );
                                })}

                                {secondaryTags.map((tag) => {
                                    const label = typeof tag === 'string' ? tag : String(tag ?? '');
                                    const normalizedLabel = normalizeTagValue(label);
                                    const isActive = normalizedSelectedTags.has(normalizedLabel);

                                    return (
                                        <TagChip
                                            key={normalizedLabel || label}
                                            label={label}
                                            value={normalizedLabel}
                                            isActive={isActive}
                                            isInteractive
                                            onToggle={onTagClick}
                                        />
                                    );
                                })}
                            </>
                        )}
                    </div>

                    {selectedTags.length > 0 && (
                        <button
                            className="clear-filters"
                            onClick={onClearFilters}
                        >
                            Remove All Tags
                        </button>
                    )}
                </div>
            </div>
        </aside>
    );
};

export default Sidebar;
